import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import throttle from 'lodash/throttle';

type UseElementRectParams<T = DOMRect> = {
  isEnabled?: boolean;
  isEqual?: (a: T, b: T) => boolean;
  selector?: (rect: DOMRectReadOnly) => T;
};

export const useElementRect = <T = DOMRect>(
  element?: HTMLElement | null,
  {
    isEnabled = true,
    isEqual = (a: T, b: T): boolean => a === b,
    selector,
  }: UseElementRectParams<T> = {}
): T | null => {
  const [elementDimensions, setElementDimensions] = useState<T | null>(null);
  useLayoutEffect(() => {
    if (element && isEnabled) {
      const rect = element.getBoundingClientRect();
      if (selector) {
        const slice = selector(rect);
        if (!elementDimensions || !isEqual(slice, elementDimensions)) {
          setElementDimensions(slice);
        }
      } else {
        setElementDimensions(rect as T);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, isEnabled]);

  const elementObserverRef = useRef<ResizeObserver>(undefined);

  const cleanObservers = (): void => {
    elementObserverRef.current?.disconnect();
    elementObserverRef.current = undefined;
  };

  useEffect(() => {
    if (!element || !isEnabled) {
      cleanObservers();
      return;
    }
    elementObserverRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      const rect = entry.target.getBoundingClientRect();
      if (selector) {
        const slice = selector(rect);
        if (!elementDimensions || !isEqual(slice, elementDimensions)) {
          setElementDimensions(slice);
        }
      } else {
        setElementDimensions(rect as T);
      }
    });
    elementObserverRef.current.observe(element);
    return cleanObservers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, isEnabled]);

  return elementDimensions;
};

export const useElementWidth = (
  element?: HTMLElement | null,
  isEnabled = true
): number | null =>
  useElementRect(element, { isEnabled, selector: ({ width }) => width });

export const useElementHeight = (
  element?: HTMLElement | null,
  isEnabled = true
): number | null =>
  useElementRect(element, { isEnabled, selector: ({ height }) => height });

type UseWindowDimensions = {
  throttleTime?: number;
  enabled?: boolean;
  width?: boolean;
  height?: boolean;
};
export const useWindowDimensions = ({
  throttleTime = 200,
  width: emitWidth = true,
  height: emitHeight = true,
  enabled = true,
}: UseWindowDimensions = {}): {
  width: number;
  height: number;
} => {
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const throttledHandleResize = throttle(() => {
      if (emitWidth) {
        setWidth(window.innerWidth);
      }
      if (emitHeight) {
        setHeight(window.innerHeight);
      }
    }, throttleTime);

    if (enabled) {
      window.addEventListener('resize', throttledHandleResize, {
        passive: true,
      });
    }

    return (): void => {
      window.removeEventListener('resize', throttledHandleResize);
    };
  }, [emitWidth, emitHeight, throttleTime, enabled]);

  return { width, height };
};

export const useWindowWidth = ({
  width = true,
  ...rest
}: Omit<UseWindowDimensions, 'height'> = {}): number => {
  return useWindowDimensions({ ...rest, width, height: false }).width;
};

export const useWindowHeight = ({
  height = true,
  ...rest
}: Omit<UseWindowDimensions, 'width'> = {}): number => {
  return useWindowDimensions({ ...rest, height, width: false }).height;
};
